import type { CatalogField, FormFieldSpec } from "../types.js";

/** Mask a PII value for display. Reveal is a separate, audited server call. */
export function maskValue(field: CatalogField, value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  const str = String(value);
  if (!field.pii || field.display !== "mask") return str;
  if (str.length <= 4) return "••••";
  return `${"•".repeat(Math.max(0, str.length - 4))}${str.slice(-4)}`;
}

/** Typed display of a field value (non-PII or already-revealed). */
export function displayValue(field: CatalogField, value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (field.type === "boolean") return value ? "Yes" : "No";
  if (field.type === "datetime") {
    let d: Date;
    if (typeof value === "number") {
      // Numeric timestamps: honour the declared format; default to seconds for small values.
      const ms = field.format === "epoch-ms" ? value : field.format === "epoch-seconds" || value < 1e11 ? value * 1000 : value;
      d = new Date(ms);
    } else {
      d = new Date(String(value));
    }
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
  }
  return String(value);
}

const STAGE_KEY = (serviceId: string): string => `admin-portal.stage.${serviceId}`;

/** Remember the selected stage per service in localStorage. */
export const stageMemory = {
  get(serviceId: string): string | null {
    try {
      return localStorage.getItem(STAGE_KEY(serviceId));
    } catch {
      return null;
    }
  },
  set(serviceId: string, stageId: string): void {
    try {
      localStorage.setItem(STAGE_KEY(serviceId), stageId);
    } catch {
      /* ignore quota/availability errors */
    }
  },
};

/**
 * Serialize form values before submitting an action. `datetime` fields come from an
 * `<input type="datetime-local">` (local wall-clock, e.g. "2026-10-30T14:30"); they are sent as an
 * ISO-8601 UTC string, which the portal API validates and converts to the backend's declared wire
 * format (manifest `format`). Empty optional datetimes are omitted. Other types pass through.
 */
export function serializeFormValues(
  fields: FormFieldSpec[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const byId = new Map(fields.map((f) => [f.id, f]));
  const out: Record<string, unknown> = {};
  for (const [id, v] of Object.entries(values)) {
    if (byId.get(id)?.type === "datetime") {
      if (v === undefined || v === null || v === "") continue;
      const ms = new Date(String(v)).getTime();
      out[id] = Number.isNaN(ms) ? v : new Date(ms).toISOString();
      continue;
    }
    out[id] = v;
  }
  return out;
}

export interface FormValidationResult {
  ok: boolean;
  errors: Record<string, string>;
}

/** Client-side mirror of the server form validation (fast feedback; server remains authoritative). */
export function validateForm(fields: FormFieldSpec[], values: Record<string, unknown>): FormValidationResult {
  const errors: Record<string, string> = {};
  const byId = new Map(fields.map((f) => [f.id, f]));

  const requiredNow = (f: FormFieldSpec): boolean => {
    if (f.required) return true;
    if (f.requiredWhen) return Object.entries(f.requiredWhen).every(([k, v]) => values[k] === v);
    return false;
  };

  for (const f of fields) {
    const v = values[f.id];
    const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
    if (empty) {
      if (requiredNow(f)) errors[f.id] = `${f.label ?? f.id} is required`;
      continue;
    }
    if ((f.type === "text" || f.type === "string") && f.minLength && String(v).length < f.minLength) {
      errors[f.id] = `${f.label ?? f.id} must be at least ${f.minLength} characters`;
    }
    const enumValues = f.ref ? byId.get(f.ref)?.values : f.values;
    if (f.type === "enum" && enumValues && !enumValues.includes(String(v))) {
      errors[f.id] = `${f.label ?? f.id} is invalid`;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}
